/**
 * IntegrationSection - Bento Grid layout with scroll animations
 * 2 hero cards (flagship features) + 4 compact cards
 */
import React from 'react';
import { Typography, Box, Container } from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import MemoryIcon from '@mui/icons-material/Memory';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { COLORS, STYLES, FEATURES, TYPOGRAPHY, SPACING, TRANSITIONS } from './landingTheme';
import { ScrollReveal, StaggerReveal } from './animations';

// Icon mapping
const ICON_MAP = {
  Psychology: PsychologyIcon,
  EventAvailable: EventAvailableIcon,
  Memory: MemoryIcon,
  ImageSearch: ImageSearchIcon,
  MedicalServices: MedicalServicesIcon,
  SupportAgent: SupportAgentIcon,
};

// Hero card — large, horizontal, for flagship features (first 2)
const HeroFeatureCard = ({ feature }) => {
  const IconComponent = ICON_MAP[feature.icon];

  return (
    <Box
      sx={{
        p: { xs: 2.5, sm: 3, md: 4 },
        borderRadius: { xs: 3, md: 4 },
        background: COLORS.white,
        border: `1px solid ${COLORS.gray200}`,
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: TRANSITIONS.normal,
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: { xs: 2, md: 3 },
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        // Decorative gradient blob
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -60,
          right: -60,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${feature.color}0a 0%, transparent 70%)`,
          transition: TRANSITIONS.normal,
          pointerEvents: 'none',
        },
        // Bottom accent bar
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${feature.color}, ${feature.color}60)`,
          borderRadius: '0 0 12px 12px',
          opacity: 0,
          transition: TRANSITIONS.normal,
        },
        '&:hover': {
          transform: { xs: 'none', sm: 'translateY(-6px)' },
          boxShadow: {
            xs: `0 4px 20px ${feature.color}18`,
            sm: `0 20px 50px ${feature.color}12, 0 8px 20px rgba(0,0,0,0.06)`,
          },
          borderColor: `${feature.color}30`,
          '&::before': {
            width: 280,
            height: 280,
            background: `radial-gradient(circle, ${feature.color}10 0%, transparent 70%)`,
          },
          '&::after': { opacity: 1 },
          '& .hero-icon': {
            transform: 'scale(1.08)',
            boxShadow: `0 12px 32px ${feature.color}20`,
          },
          '& .hero-arrow': {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
      }}
    >
      {/* Large icon */}
      <Box
        className="hero-icon"
        sx={{
          width: { xs: 56, sm: 64, md: 72 },
          height: { xs: 56, sm: 64, md: 72 },
          minWidth: { xs: 56, sm: 64, md: 72 },
          borderRadius: { xs: 3, md: 4 },
          background: `linear-gradient(135deg, ${feature.color}14 0%, ${feature.color}08 100%)`,
          border: `1px solid ${feature.color}18`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: TRANSITIONS.normal,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {IconComponent && (
          <IconComponent sx={{ fontSize: { xs: 28, sm: 32, md: 36 }, color: feature.color }} />
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, position: 'relative', zIndex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography
            sx={{
              fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.25rem' },
              fontWeight: 700,
              color: COLORS.textPrimary,
              lineHeight: 1.3,
            }}
          >
            {feature.title}
          </Typography>
          <ArrowForwardIcon
            className="hero-arrow"
            sx={{
              fontSize: 20,
              color: feature.color,
              opacity: 0,
              transform: 'translateX(-8px)',
              transition: TRANSITIONS.normal,
              display: { xs: 'none', sm: 'block' },
              flexShrink: 0,
              ml: 1,
            }}
          />
        </Box>
        <Typography
          sx={{
            color: COLORS.textSecondary,
            fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
            lineHeight: 1.7,
          }}
        >
          {feature.description}
        </Typography>
      </Box>
    </Box>
  );
};

// Compact card — smaller, for supporting features (last 4)
const CompactFeatureCard = ({ feature }) => {
  const IconComponent = ICON_MAP[feature.icon];

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: { xs: 3, md: 4 },
        background: COLORS.white,
        border: `1px solid ${COLORS.gray200}`,
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: TRANSITIONS.normal,
        height: '100%',
        display: 'flex',
        flexDirection: { xs: 'row', sm: 'column' },
        alignItems: { xs: 'flex-start', sm: 'stretch' },
        gap: { xs: 1.5, sm: 0 },
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        // Left accent bar
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: 3,
          bottom: 0,
          background: feature.color,
          borderRadius: '12px 0 0 12px',
          opacity: 0,
          transition: TRANSITIONS.normal,
        },
        '&:hover': {
          transform: { xs: 'none', sm: 'translateY(-5px)' },
          boxShadow: {
            xs: `0 4px 16px ${feature.color}18`,
            sm: `0 16px 40px ${feature.color}10, 0 6px 12px rgba(0,0,0,0.05)`,
          },
          borderColor: `${feature.color}30`,
          '&::after': { opacity: 1 },
          '& .compact-icon': {
            transform: 'scale(1.1)',
            boxShadow: `0 8px 20px ${feature.color}20`,
          },
        },
      }}
    >
      {/* Icon */}
      <Box
        className="compact-icon"
        sx={{
          width: { xs: 40, sm: 44 },
          height: { xs: 40, sm: 44 },
          minWidth: { xs: 40, sm: 44 },
          borderRadius: 2.5,
          background: `linear-gradient(135deg, ${feature.color}14 0%, ${feature.color}08 100%)`,
          border: `1px solid ${feature.color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: { xs: 0, sm: 2 },
          transition: TRANSITIONS.normal,
        }}
      >
        {IconComponent && (
          <IconComponent sx={{ fontSize: { xs: 20, sm: 22 }, color: feature.color }} />
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: { xs: '0.9rem', sm: '0.95rem' },
            fontWeight: 700,
            color: COLORS.textPrimary,
            lineHeight: 1.3,
            mb: 0.5,
          }}
        >
          {feature.title}
        </Typography>
        <Typography
          sx={{
            color: COLORS.textSecondary,
            fontSize: { xs: '0.78rem', sm: '0.82rem' },
            lineHeight: 1.6,
          }}
        >
          {feature.description}
        </Typography>
      </Box>
    </Box>
  );
};

export default function IntegrationSection() {
  const heroFeatures = FEATURES.slice(0, 2);
  const compactFeatures = FEATURES.slice(2);

  return (
    <Box sx={{ py: SPACING.sectionY, background: COLORS.white, position: 'relative', overflow: 'hidden' }}>
      {/* Subtle decorative blob */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%', right: '-10%',
          width: 400, height: 400,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.04) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg">
        {/* Section header */}
        <ScrollReveal>
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography
              sx={{
                color: COLORS.accent,
                fontSize: TYPOGRAPHY.tiny,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 1.5,
              }}
            >
              Funcionalidades
            </Typography>
            <Typography
              variant="h2"
              sx={{ ...STYLES.sectionTitle }}
            >
              Diseñado para profesionales de salud
            </Typography>
            <Typography
              sx={{ ...STYLES.sectionSubtitle }}
            >
              Todo lo que tu consultorio necesita en un solo asistente de WhatsApp
            </Typography>
          </Box>
        </ScrollReveal>

        {/* Hero row — 2 large cards */}
        <StaggerReveal
          staggerDelay={0.15}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: { xs: 2.5, sm: 3, md: 4 },
            mb: { xs: 2.5, sm: 3, md: 4 },
          }}
        >
          {heroFeatures.map((feature, idx) => (
            <HeroFeatureCard key={idx} feature={feature} />
          ))}
        </StaggerReveal>

        {/* Compact row — 4 smaller cards */}
        <StaggerReveal
          staggerDelay={0.1}
          baseDelay={0.1}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: { xs: 2.5, sm: 3, md: 4 },
          }}
        >
          {compactFeatures.map((feature, idx) => (
            <CompactFeatureCard key={idx} feature={feature} />
          ))}
        </StaggerReveal>
      </Container>
    </Box>
  );
}
